<script setup lang="ts">
import type { MixTrackDto } from '../../dtos'
import { useMixStore, pixelsPerSecond } from '../../stores/mixStore'
import AudioClipRegion from './AudioClipRegion.vue'
import { ref, computed, onMounted, onUnmounted, inject, type Ref } from 'vue'

const props = withDefaults(defineProps<{
  track: MixTrackDto
  mixDurationSeconds: number
  horizontal?: boolean
  readonly?: boolean
}>(), {
  horizontal: false,
  readonly: false
})

const mixStore = useMixStore()
const fileInput = ref<HTMLInputElement | null>(null)
const uploadTime = ref(0)

const snapToBars = inject<Ref<boolean>>('snapToBars', ref(false))

const trackLength = computed(() => {
  return props.mixDurationSeconds * pixelsPerSecond.value
})

const beatLines = computed(() => {
  if (!snapToBars.value) return []
  const bpm = Math.max(20, mixStore.metronomeBpm || 120)
  const beatsPerBar = Math.max(1, mixStore.metronomeBeatsPerBar || 4)
  const totalSeconds = props.mixDurationSeconds
  const secondsPerBeat = 60 / bpm
  const result: { offset: number; isBar: boolean }[] = []
  const totalBeats = Math.floor(totalSeconds / secondsPerBeat)
  for (let i = 1; i <= totalBeats; i++) {
    result.push({
      offset: i * secondsPerBeat * pixelsPerSecond.value,
      isBar: i % beatsPerBar === 0
    })
  }
  return result
})

const isRecordingThisTrack = computed(() =>
  mixStore.isRecording && mixStore.armedTrackId === props.track.id
)

const recordGhostStyle = computed(() => {
  const start = mixStore.recordStartTime * pixelsPerSecond.value
  const length = Math.max(0, (mixStore.currentTime - mixStore.recordStartTime) * pixelsPerSecond.value)
  return props.horizontal
    ? { left: `${start}px`, width: `${length}px`, top: '0', bottom: '0' }
    : { top: `${start}px`, height: `${length}px`, left: '0', right: '0' }
})

const isDragDestination = computed(() => {
  const ds = mixStore.dragState
  return !!ds && ds.destTrackId === props.track.id && ds.sourceTrackId !== props.track.id
})

const dragGhostStyle = computed(() => {
  const ds = mixStore.dragState
  if (!ds) return {}
  const start = ds.timelineStart * pixelsPerSecond.value
  const length = ds.duration * pixelsPerSecond.value
  return props.horizontal
    ? { left: `${start}px`, width: `${length}px`, top: '0', bottom: '0' }
    : { top: `${start}px`, height: `${length}px`, left: '0', right: '0' }
})

const handleTriggerUpload = (e: any) => {
  if (props.readonly) return
  if (e.detail.trackId === props.track.id) {
    uploadTime.value = e.detail.time
    fileInput.value?.click()
  }
}

onMounted(() => {
  window.addEventListener('trigger-upload', handleTriggerUpload)
})

onUnmounted(() => {
  window.removeEventListener('trigger-upload', handleTriggerUpload)
})

const onFileSelected = async (e: Event) => {
  const target = e.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    await mixStore.addClip(props.track.id, file, uploadTime.value)
    target.value = ''
  }
}

const handleLaneClick = (e: MouseEvent) => {
  const container = document.getElementById('tracks-container')
  if (!container) return

  const rect = container.getBoundingClientRect()
  let time: number

  if (props.horizontal) {
    const x = e.clientX - rect.left + container.scrollLeft
    time = Math.max(0, x / pixelsPerSecond.value)
  } else {
    const y = e.clientY - rect.top + container.scrollTop
    time = Math.max(0, y / pixelsPerSecond.value)
  }

  mixStore.seek(time)
}
</script>

<template>
  <div class="group relative" :class="horizontal ? 'h-24 shrink-0' : 'w-16 shrink-0'">
    <div
      class="relative bg-white group-hover:bg-slate-50/50 transition-colors track-lane"
      :class="horizontal ? 'h-full border-b border-slate-300' : 'border-r border-slate-300'"
      :style="horizontal ? { width: `${trackLength}px` } : { height: `${trackLength}px` }"
      :data-track-id="track.id"
      @click="handleLaneClick"
    >
      <!-- Grid lines: per-second by default, bar/beat lines when snap is on -->
      <div v-if="!snapToBars" class="absolute inset-0 pointer-events-none" :class="horizontal ? 'flex flex-row' : 'flex flex-col'">
        <div v-for="i in mixDurationSeconds" :key="i"
             class="timeline-grid-line"
             :class="horizontal ? 'border-l border-slate-200 h-full' : 'border-t border-slate-200 w-full'"
             :style="horizontal
               ? { width: `${pixelsPerSecond}px`, flexShrink: 0 }
               : { height: `${pixelsPerSecond}px`, flexShrink: 0 }">
        </div>
      </div>
      <div v-else class="absolute inset-0 pointer-events-none">
        <div v-for="(line, idx) in beatLines" :key="idx"
             class="absolute bg-slate-200"
             :class="[
               horizontal
                 ? (line.isBar ? 'top-0 bottom-0 w-1 timeline-bar-line' : 'top-0 bottom-0 w-px timeline-beat-line')
                 : (line.isBar ? 'left-0 right-0 h-1 timeline-bar-line' : 'left-0 right-0 h-px timeline-beat-line')
             ]"
             :style="horizontal ? { left: `${line.offset}px` } : { top: `${line.offset}px` }">
        </div>
      </div>

      <!-- Cross-track drag preview ghost -->
      <div
        v-if="isDragDestination"
        class="absolute bg-teal-400/30 border-2 border-dashed border-teal-500 rounded pointer-events-none z-40"
        :style="dragGhostStyle"
      ></div>

      <!-- Recording ghost -->
      <div
        v-if="isRecordingThisTrack"
        class="absolute bg-red-500/20 border border-red-500/60 rounded pointer-events-none flex items-center justify-center overflow-hidden"
        :style="recordGhostStyle"
      >
        <div class="absolute inset-0 bg-red-500/10 animate-pulse"></div>
        <span class="relative text-[10px] font-bold text-red-600 uppercase tracking-wider flex items-center gap-1">
          <span class="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
          REC
        </span>
      </div>

      <!-- Clips -->
      <AudioClipRegion
        v-for="clip in [...track.clips].sort((a, b) => a.timelineStart - b.timelineStart)"
        :key="clip.id"
        :clip="clip"
        :horizontal="horizontal"
        :readonly="readonly"
      />

      <input
        type="file"
        ref="fileInput"
        class="hidden"
        accept="audio/*"
        @change="onFileSelected"
        @click.stop
      />
    </div>
  </div>
</template>
